import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PublicLayout from "../components/PublicLayout";
import { useLang } from "../context/LanguageContext";
import { useDashT } from "../lib/dashboardStrings";
import { setPageMeta } from "../lib/seo";
import { renderMarkdown, stripMarkdown } from "../lib/markdown";

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "sv" ? "sv-SE" : "en-GB",
      { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
};

const ORIGIN = "https://talentsio.com";

const SpotlightArticle = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const { lang } = useLang();
  const dt = useDashT();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let alive = true;
    setState("loading");
    api.get(`/spotlight/${slug}`)
      .then((r) => { if (alive) { setPost(r.data); setState("ok"); } })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const desc = post.excerpt || stripMarkdown(post.body_md, 160);
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: desc,
      image: post.hero_image_url ? [post.hero_image_url] : undefined,
      datePublished: post.publish_at,
      dateModified: post.updated_at,
      author: { "@type": "Organization", name: post.author || "Talentsio" },
      publisher: {
        "@type": "Organization",
        name: "Talentsio",
        logo: { "@type": "ImageObject", url: `${ORIGIN}/icon-512.png` },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${ORIGIN}/spotlight/${post.slug}`,
      },
      inLanguage: post.lang === "sv" ? "sv-SE" : "en-GB",
    };
    setPageMeta({
      title: `${post.title} | Talentsio Spotlight`,
      description: desc,
      lang: post.lang || "en",
      path: `/spotlight/${post.slug}`,
      image: post.hero_image_url || "/og/entertainment.jpg",
      jsonLd,
    });
  }, [post]);

  if (state === "loading") {
    return (
      <PublicLayout>
        <main className="max-w-[820px] mx-auto px-6 pt-[110px] pb-20">
          <div className="h-6 w-32 bg-[#232323] animate-pulse rounded mb-4" />
          <div className="h-12 bg-[#232323] animate-pulse rounded mb-3" />
          <div className="h-12 bg-[#232323] animate-pulse rounded w-4/5 mb-6" />
          <div className="aspect-[16/9] bg-[#1f1f1f] animate-pulse rounded-2xl" />
        </main>
      </PublicLayout>
    );
  }
  if (state === "notfound" || !post) {
    return (
      <PublicLayout>
        <main className="max-w-[640px] mx-auto px-6 pt-[150px] pb-28 text-center">
          <h1 className="text-white text-[32px] font-bold mb-3">
            {dt("Article not found")}
          </h1>
          <p className="text-neutral-400 mb-6">
            {dt("Perhaps it was unpublished or moved.")}
          </p>
          <button onClick={() => nav("/spotlight")} data-testid="spotlight-back"
            className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-6 py-3 rounded-xl transition-colors">
            <ArrowLeft size={16} /> {dt("Back to Spotlight")}
          </button>
        </main>
      </PublicLayout>
    );
  }

  const share = async () => {
    const url = `${window.location.origin}/spotlight/${post.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, text: post.excerpt || "", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(dt("Link copied"));
      }
    } catch { /* ignored */ }
  };

  return (
    <PublicLayout>
      <article className="pt-[80px] pb-24" data-testid="spotlight-article">
        {/* Hero */}
        <header className="max-w-[820px] mx-auto px-6 pt-8">
          <Link to="/spotlight" data-testid="spotlight-back"
            className="inline-flex items-center gap-1 text-[13px] text-neutral-500 hover:text-neutral-300 mb-6">
            <ArrowLeft size={14} /> {dt("All articles")}
          </Link>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.14em] text-[#FF7A2F] font-bold uppercase mb-4">
            <Sparkles size={12} /> TALENTSIO SPOTLIGHT
          </div>
          <h1 data-testid="spotlight-h1"
            className="text-white font-extrabold tracking-tight leading-[1.08] text-[40px] md:text-[52px] mb-5">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-neutral-300 text-[20px] leading-[1.55] mb-6 max-w-[720px]">
              {post.excerpt}
            </p>
          )}
          <div className="flex items-center gap-4 text-[13px] text-neutral-500 mb-8">
            <span className="flex items-center gap-1"><Calendar size={13} /> {fmtDate(post.publish_at, post.lang)}</span>
            {post.author && <span>· {post.author}</span>}
            <button onClick={share} data-testid="spotlight-share"
              className="ml-auto inline-flex items-center gap-1 text-neutral-400 hover:text-white transition-colors">
              <Share2 size={14} /> {dt("Share")}
            </button>
          </div>
        </header>

        {post.hero_image_url && (
          <div className="max-w-[1000px] mx-auto px-6 mb-10">
            <img src={post.hero_image_url} alt={post.hero_alt || post.title}
              className="w-full aspect-[16/9] object-cover rounded-2xl"
              fetchPriority="high" decoding="async" />
          </div>
        )}

        <div className="max-w-[720px] mx-auto px-6 spotlight-prose"
             data-testid="spotlight-body"
             dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body_md) }} />

        {/* CTA */}
        <div className="max-w-[720px] mx-auto px-6 mt-14">
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-7 text-center">
            <h2 className="text-white text-[22px] font-bold mb-2">
              {dt("Book the right entertainment on Talentsio")}
            </h2>
            <p className="text-neutral-400 text-[15px] leading-relaxed mb-4">
              {dt("Post a free request and get priced offers from verified artists directly.")}
            </p>
            <Link to="/BookingRequest" data-testid="spotlight-cta"
              className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-6 py-3 rounded-xl transition-colors">
              {dt("Post request")}
            </Link>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
};

export default SpotlightArticle;
